import { useState, useRef, useEffect } from 'react'

interface UseSwiperNavigationProps {
  numberOfVisibleSlides: number
  childrenCount: number
  spaceBetween: number
  isVertical: boolean
  afterChange?: (data: { swipeDirection: string | null; activeIndex: number }) => void
  initialSlide?: number
}

export function useSwiperNavigation({
  spaceBetween,
  afterChange,
  isVertical,
  initialSlide = 0,
}: UseSwiperNavigationProps) {
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const previousScrollLeft = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const initialSlideRef = useRef(initialSlide)
  const isInitialSlideSet = useRef(false)

  const handleScroll = () => {
    const container = containerRef.current

    if (!container || !isInitialSlideSet.current) return

    const scrollLeft = container.scrollLeft
    const scrollWidth = container.scrollWidth
    const clientWidth = container.clientWidth
    setIsBeginning(scrollLeft === 0)
    // NOTE: Math.ceil to counter the rem to fractional pixel division
    setIsEnd(Math.ceil(scrollLeft + clientWidth) >= scrollWidth)

    let swipeDirection = null
    if (previousScrollLeft.current < scrollLeft) swipeDirection = 'next'
    else if (previousScrollLeft.current > scrollLeft) swipeDirection = 'prev'

    previousScrollLeft.current = scrollLeft

    const slideWidth = container?.firstChild?.clientWidth ?? 0 + parseInt((spaceBetween ?? 0).toString(), 10)

    const newActiveIndex = Math.round(scrollLeft / slideWidth)
    setActiveIndex(newActiveIndex)

    if (afterChange) afterChange({ swipeDirection, activeIndex: newActiveIndex })
  }

  const scrollToNext = () => {
    const container = containerRef.current
    if (container) {
      if (isVertical) {
        container.scrollBy({ top: container.clientHeight, behavior: 'smooth' })
      } else container.scrollBy({ left: container.clientWidth, behavior: 'smooth' })
    }
  }

  const scrollToPrev = () => {
    const container = containerRef.current
    if (container) {
      if (isVertical) {
        container.scrollBy({ top: -container.clientHeight, behavior: 'smooth' })
      } else container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) container.addEventListener('scroll', handleScroll)
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth =
        (containerRef.current?.firstChild?.firstChild as HTMLElement)?.clientWidth +
        parseInt((spaceBetween ?? 0).toString(), 10)
      const scrollPosition = itemWidth * initialSlideRef.current

      containerRef.current?.scrollTo({
        left: isVertical ? 0 : scrollPosition,
        top: isVertical ? scrollPosition : 0,
        behavior: 'instant',
      })
      isInitialSlideSet.current = true
    }
  }, [isVertical, spaceBetween])

  return {
    containerRef,
    activeIndex,
    isBeginning,
    isEnd,
    scrollToNext,
    scrollToPrev,
    handleScroll,
  }
}
